/**
 * Minimal JWT payload decoding for client-side use.
 * Does not verify the token; the token is assumed to come from a trusted source (Auth0 SDK).
 */

export interface JwtPayload {
  iat?: number;
  exp?: number;
  sub?: string;
  [key: string]: unknown;
}

const BASE64URL_REGEX = /^[A-Za-z0-9_-]+$/;

/**
 * Decodes the payload of a JWT without verification.
 * Returns an object with at least { iat?: number }.
 * On malformed token, returns { iat: 0 } so consumers treat it as "refresh needed".
 */
export function decodeJwtPayload(token: string): JwtPayload {
  try {
    if (!token || typeof token !== "string") {
      return { iat: 0 };
    }
    const parts = token.split(".");
    if (parts.length !== 3) {
      return { iat: 0 };
    }
    const payloadPart = parts[1];
    if (!payloadPart || !BASE64URL_REGEX.test(payloadPart.replace(/=/g, ""))) {
      return { iat: 0 };
    }
    const padded = payloadPart + "==".slice(0, (4 - (payloadPart.length % 4)) % 4);
    const decoded = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(decoded) as Record<string, unknown>;
    return typeof parsed === "object" && parsed !== null ? (parsed as JwtPayload) : { iat: 0 };
  } catch {
    return { iat: 0 };
  }
}

/**
 * Returns the "issued at" (iat) claim in milliseconds, or 0 if missing/invalid.
 */
export function getTokenIssuedAtMs(token: string): number {
  const payload = decodeJwtPayload(token);
  const iat = payload.iat;
  if (typeof iat !== "number" || iat <= 0) {
    return 0;
  }
  return iat * 1000;
}
